import { Injectable } from '@angular/core';
import { ManagementStoreService } from './management-store.service';
import { ManagementApiService } from '@emm/management/services/management-api.service';
import {BehaviorSubject, Observable, ReplaySubject} from 'rxjs';
import {NutritionAssignment, NutritionSaveRequest} from '@emm/core/models/nutrition.model';
import {NotificationService} from '@emm/shared/services/notification.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {take} from 'rxjs/operators';
import {UserService} from '@emm/core/services/user.service';

@Injectable({ providedIn: 'root' })
export class NutritionFacadeService {

    assignment$ = new BehaviorSubject<any>(null);
    centerLoading$ = this.managementStore.queryNutrition.centerLoading$;
    location$ = new BehaviorSubject<any>(null);
    location;
    assignments$ = new ReplaySubject<NutritionAssignment[]>(null);
    locationSetType$ = this.managementStore.queryNutrition.locationSetType$;
    locationSetSuccess$ = this.managementStore.queryLocationSets.success$.subscribe(() => {
        this.managementStore.dispatch.selectNutritionLocationSetTypeSuccess();
    });
    profileOptions$ = new BehaviorSubject<any>(null);
    rightLoading$ = this.managementStore.queryNutrition.rightLoading$;
    selectedDate$ = this.managementStore.queryNutrition.selectedDate$;
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isSuperAdminOrAdmin = this.userService.hasSuperAdminOrAdminRole();
    showPublish$ = new BehaviorSubject<boolean>(this.isSuperAdminOrAdmin);

    constructor(private managementStore: ManagementStoreService,
                private api: ManagementApiService,
                private notificationService: NotificationService,
                private userService: UserService) {}

    onDestroy(): void {
        this.locationSetSuccess$.unsubscribe();
    }

    initNutrition(): void {
        this.managementStore.dispatch.initNutrition();
    }

    updateNutritionAssignments(): void {
        const response = this.api.getNutritionAssignments(this.location?.tag)
        response.subscribe({
                next: (assignments) => {
                    this.notificationService.success('Successfully saved assignment.');
                    this.assignments$.next(assignments);
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage)
                }
            }
        );
    }

    selectedLocation(location): void {
        this.location$.next(location);
        this.location = location;
        this.api.getNutritionAssignments(location.tag).subscribe(
            (assignments) => {
                this.assignments$.next(assignments);
                if (this.isAdmin || this.isSuperAdmin) this.showPublish$.next(true);
            });
    }

    selectedLocationSet(locationSet): Observable<NutritionAssignment[]> {
        this.location$.next(locationSet);
        this.location = locationSet;
        const response = this.api.getNutritionAssignments(locationSet.tag)
        response.subscribe(assignments => {
                this.assignments$.next(assignments);
            if ((locationSet?.tag === 'CHAIN' || locationSet?.tag === 'LOCATION' || !isNaN(Number(locationSet.tag))) &&
                (this.userService.hasSuperAdminOrAdminRole())) {
                this.showPublish$.next(true);
            } else {
                this.showPublish$.next(false);
            }
        });
        return response;
    }

    selectedNutritionLocationSetType(locationSetType): void {
        this.managementStore.dispatch.selectNutritionLocationSetType(locationSetType);
    }

    selectedDate(date): void {
        this.managementStore.dispatch.selectDate(date);
    }

    clearedLocation(): void {
        this.managementStore.dispatch.unselectLocation();
    }

    loadProfileOptions(): void {
        this.api.getProfileOptions().subscribe(result => {
            this.profileOptions$.next(result);
        });
    }

    loadAssignments(): void {
        this.api.getNutritionAssignments(this.location?.tag).subscribe(result => {
            this.assignments$.next(result);
        })
    }

    selectedAssignment(assignment): void {
        this.assignment$.next(assignment);
    }

    saveLocationAssignment(apiData: NutritionSaveRequest): void {
        this.api.saveLocationAssignment(apiData).subscribe({
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage)
                }
            }
        );
        this.updateNutritionAssignments();
    }

    getLocationInfo() {
        let locationType;
        let location;
        this.locationSetType$.pipe(take(1)).subscribe(locationSetType => locationType = locationSetType);
        this.location$.pipe(take(1)).subscribe(_location => location = _location);
        return {locationType, location}
    }

    publishNutrition(locationType: string, location: {name: string, tag: string}) {
        return this.api.publishNutrition(locationType, location);
    }

}
